import { createMutation, createQuery } from "react-query-kit";
import { api } from "../../api";
import {
  CreatePackageReservationExternalRequest,
  CreateReservationExternalRequest,
  CreateActivityReservationExternalRequest,
  CreateReservationExternalResponse,
  GetReservationExternalResponse,
  GuestDetails,
  Holder,
  PatchReservationExternalRequest,
  PatchReservationWithRowRequest,
  PreviewRowRequest,
} from "../../contracts/reservation";
import { queryClient } from "../../../query-client";
import { LanguageCode } from "../../contracts/language-settings";
import { handleMutationError } from "../../../utils/apiUtils";
import {
  SurfboardPaymentCredentials,
  SurfboardPaymentStatusResponse,
} from "../../../types/surfboard";
import { ApiError } from "../../api-backend";
import {
  PaymentReceiptResponse,
  PaymentLinkLineItemsResponse,
} from "../../contracts/payment";
import { applyOptimisticReservationPatch } from "../../../utils/applyOptimisticReservationPatch";
import { shouldRetryPayment } from "./shouldRetryPayment";

const RETRY_DELAY_MS = 1000;
const AVAILABILITY_CALENDAR_QUERY_KEY = ["useGetAvailabilityCalendar"] as const;

// POST /api/public/reservations/{siteId}
export const useCreateReservation = createMutation<
  CreateReservationExternalResponse,
  (
    | CreateReservationExternalRequest
    | CreateActivityReservationExternalRequest
  ) & {
    languageCode: LanguageCode;
    siteId: string;
  }
>({
  mutationFn: async (data) => {
    return api.post<CreateReservationExternalResponse>(
      `/api/public/reservations/${data.siteId}/?languageCode=${data.languageCode}`,
      data,
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["useGetReservation"],
    });
    queryClient.invalidateQueries({
      queryKey: ["useGetCategories"],
    });
    queryClient.invalidateQueries({
      queryKey: AVAILABILITY_CALENDAR_QUERY_KEY,
    });
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

// POST /api/public/reservations/{siteId}
// Package-create branch: applies one package across N units atomically.
// Shares the single-category create endpoint — the backend discriminates on
// the `packageReservation` field (see CreateReservationOrPackageExternalRequest).
//
// Deliberately omits an `onError` toast: the caller (PackageDetailDialog)
// inspects the error key to drive package-specific recovery (re-fetch detail on
// a stale version, keep the guest in the flow on a just-sold-out race) rather
// than firing the generic mutation-error toast.
export const useCreatePackageReservation = createMutation<
  CreateReservationExternalResponse,
  CreatePackageReservationExternalRequest & {
    languageCode: LanguageCode;
    siteId: string;
  }
>({
  mutationFn: async ({ siteId, languageCode, packageReservation }) => {
    return api.post<CreateReservationExternalResponse>(
      `/api/public/reservations/${siteId}/?languageCode=${languageCode}`,
      { packageReservation },
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["useGetReservation"],
    });
    queryClient.invalidateQueries({
      queryKey: ["useGetCategories"],
    });
    queryClient.invalidateQueries({
      queryKey: ["useGetPackages"],
    });
    queryClient.invalidateQueries({
      queryKey: AVAILABILITY_CALENDAR_QUERY_KEY,
    });
  },
});

// GET /api/public/reservations/{siteId}/{reservationKey}
export const useGetReservation = createQuery<
  GetReservationExternalResponse,
  { reservationKey: string; languageCode: LanguageCode; siteId: string }
>({
  queryKey: ["useGetReservation"],
  fetcher: async ({ reservationKey, languageCode, siteId }) => {
    return api.get<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}?languageCode=${languageCode}`,
    );
  },
  enabled: false,
});

// PATCH /api/public/reservations/{siteId}/{reservationKey}
const GET_RESERVATION_QUERY_KEY = ["useGetReservation"] as const;

// Tracks how many reservation-mutating requests are currently in flight.
// We defer `invalidateQueries` until the counter reaches zero so a mid-burst
// refetch can't read partial server state and clobber the accumulated
// optimistic cache (the wrong-state flash on slow networks).
//
// Increment lives at the top of `onMutate` (synchronous, before any await) and
// decrement lives in `onSettled`. `onSettled` is the only lifecycle callback
// guaranteed to fire for every mutation outcome (success, error, even when
// `onMutate` throws mid-way), so the counter can't leak.
let pendingReservationMutations = 0;
let idleWaiters: Array<() => void> = [];

const beginReservationMutation = (): void => {
  pendingReservationMutations++;
};

/** Resolves once no reservation PATCH is in flight, so a full-row write can read what they wrote. */
export const reservationMutationsIdle = (): Promise<void> =>
  pendingReservationMutations === 0
    ? Promise.resolve()
    : new Promise((resolve) => idleWaiters.push(resolve));

const settleReservationMutation = (rollback: (() => void) | null): void => {
  pendingReservationMutations = Math.max(0, pendingReservationMutations - 1);
  if (pendingReservationMutations === 0) {
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
    rollback?.();
    queryClient.invalidateQueries({ queryKey: GET_RESERVATION_QUERY_KEY });
  }
};

const rollbackFromContext = (
  context:
    | {
        previous: [
          readonly unknown[],
          GetReservationExternalResponse | undefined,
        ][];
      }
    | undefined,
): (() => void) | null =>
  context
    ? () => {
        for (const [key, value] of context.previous) {
          queryClient.setQueryData(key, value);
        }
      }
    : null;

export const __resetReservationMutationCounterForTests = (): void => {
  pendingReservationMutations = 0;
  idleWaiters = [];
};

type UpdateReservationVariables = {
  reservationKey: string;
  data: Partial<PatchReservationExternalRequest>;
  siteId: string;
  languageCode: LanguageCode;
};

type UpdateReservationContext = {
  previous: [readonly unknown[], GetReservationExternalResponse | undefined][];
};

export const useUpdateReservation = createMutation<
  GetReservationExternalResponse,
  UpdateReservationVariables,
  Error,
  UpdateReservationContext
>({
  mutationFn: async ({ reservationKey, data, siteId, languageCode }) => {
    return api.patch<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}`,
      data,
      { languageCode },
    );
  },
  onMutate: async ({ data }): Promise<UpdateReservationContext> => {
    beginReservationMutation();
    await queryClient.cancelQueries({ queryKey: GET_RESERVATION_QUERY_KEY });
    const previous = queryClient.getQueriesData<GetReservationExternalResponse>(
      {
        queryKey: GET_RESERVATION_QUERY_KEY,
      },
    );
    if (data.rows && data.rows.length > 0) {
      const patchRows = data.rows;
      queryClient.setQueriesData<GetReservationExternalResponse>(
        { queryKey: GET_RESERVATION_QUERY_KEY },
        (current) =>
          current
            ? applyOptimisticReservationPatch(current, patchRows)
            : current,
      );
    }
    return { previous };
  },
  onError: (error) => {
    handleMutationError(error);
  },
  onSettled: (_data, error, _variables, context) => {
    settleReservationMutation(error ? rollbackFromContext(context) : null);
  },
});

// PATCH /api/public/reservations/{siteId}/{reservationKey}/notes
export const useAddNoteToReservation = createMutation<
  GetReservationExternalResponse,
  {
    reservationKey: string;
    data: { note: string };
    siteId: string;
  }
>({
  mutationFn: async ({ reservationKey, data, siteId }) => {
    return api.patch<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/notes`,
      data,
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["useGetReservation"],
    });
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

// PATCH /api/public/reservations/{siteId}/{reservationKey}/guests
export const useUpdateGuestDetails = createMutation<
  GetReservationExternalResponse,
  {
    reservationKey: string;
    data: {
      rows: Array<{
        rowId: string;
        contactId: string;
        sameAsHolder: boolean;
        guestDetails?: Omit<Holder, "id" | "contactId">;
        additionalGuests?: Array<{
          contactId: string;
          guestDetails: Omit<Holder, "id" | "contactId">;
        }>;
      }>;
    };
    siteId: string;
  }
>({
  mutationFn: async ({ reservationKey, data, siteId }) => {
    return api.patch<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/guests`,
      data,
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["useGetReservation"],
    });
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

// PATCH /api/public/reservations/{siteId}/{reservationKey}/finalize
export const useFinalizeReservation = createMutation<
  GetReservationExternalResponse,
  {
    reservationKey: string;
    data: { state: "confirmed" };
    siteId: string;
  }
>({
  mutationFn: async ({ reservationKey, data, siteId }) => {
    return api.patch<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/finalize`,
      data,
    );
  },
  onSuccess: () => {
    // Refresh the cached reservation so isFinalized flips to true. Callers
    // that keep rendering the confirmation page after finalize (cold reload,
    // or any future read-only flow) then correctly skip the expiry timer.
    queryClient.invalidateQueries({ queryKey: GET_RESERVATION_QUERY_KEY });
  },
  onError: (error) => {
    // 404 means the key is gone (e.g. retention cleanup ran, or an older
    // backend hard-deleted on finalize) — the reservation is already in its
    // desired end state, so silently succeed instead of alarming the guest.
    if (error instanceof ApiError && error.status === 404) return;
    handleMutationError(error);
  },
});

// PATCH /api/public/reservations/{siteId}/{reservationKey}/expire
export const useExpireReservation = createMutation<
  GetReservationExternalResponse,
  { siteId: string; reservationKey: string }
>({
  mutationFn: async ({ siteId, reservationKey }) => {
    return api.patch<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/expire`,
      {},
    );
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

// POST /api/public/reservations/{siteId}/{reservationKey}/clone
// Recreates an identical reservation when the previous one expired.
// Returns the new reservationKey (or the same key if the source was still initiated).
export const useCloneReservation = createMutation<
  CreateReservationExternalResponse,
  { siteId: string; reservationKey: string; languageCode: LanguageCode }
>({
  mutationFn: async ({ siteId, reservationKey, languageCode }) => {
    return api.post<CreateReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/clone`,
      {},
      { languageCode },
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["useGetReservation"],
    });
    queryClient.invalidateQueries({
      queryKey: ["useGetCategories"],
    });
    queryClient.invalidateQueries({
      queryKey: AVAILABILITY_CALENDAR_QUERY_KEY,
    });
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

// POST /api/public/reservations/{siteId}/{reservationKey}/row
export const useAddRowToReservation = createMutation<
  GetReservationExternalResponse,
  {
    reservationKey: string;
    data: PatchReservationWithRowRequest;
    siteId: string;
  }
>({
  mutationFn: async ({ reservationKey, data, siteId }) => {
    return api.post<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/row`,
      data,
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["useGetReservation"],
    });
    queryClient.invalidateQueries({
      queryKey: ["useGetCategories"],
    });
    queryClient.invalidateQueries({
      queryKey: AVAILABILITY_CALENDAR_QUERY_KEY,
    });
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

/**
 * POST /api/public/reservations/{siteId}/{reservationKey}/row-preview
 *
 * Dry run: will this room actually get the discount? A catalog search can't check
 * the code's shared room pool, so a card can advertise a discount that is already
 * gone — this is how the guest hears about it before committing rather than from a
 * higher price afterwards.
 *
 * No invalidation and no error toast: it changes nothing, and a failed preview
 * must not put a message in front of a guest whose booking is fine.
 */
export const usePreviewReservationRow = createMutation<
  { roomGetsDiscount: boolean },
  {
    reservationKey: string;
    siteId: string;
    data: PreviewRowRequest;
  }
>({
  mutationFn: async ({ reservationKey, siteId, data }) => {
    return api.post<{ roomGetsDiscount: boolean }>(
      `/api/public/reservations/${siteId}/${reservationKey}/row-preview`,
      data,
    );
  },
});

/**
 * POST /api/public/reservations/{siteId}/{reservationKey}/rows/{rowId}/party-fit
 *
 * Dry run: would saving this party on a booked row be refused because no free unit of its
 * room takes the extra beds the party needs? Changes nothing, so no invalidation.
 */
export const useRowPartyFit = createQuery<
  { fits: boolean },
  { siteId: string; reservationKey: string; rowId: string; guests: GuestDetails }
>({
  queryKey: ["useRowPartyFit"],
  fetcher: ({ siteId, reservationKey, rowId, guests }) =>
    api.post<{ fits: boolean }>(
      `/api/public/reservations/${siteId}/${reservationKey}/rows/${rowId}/party-fit`,
      { guests },
    ),
});

// POST /api/public/payment/{siteId}/start/wbe/{reservationKey}
export const useStartWbePayment = createMutation<
  SurfboardPaymentCredentials,
  {
    reservationKey: string;
    siteId: string;
    contact: {
      email: string;
      phone: string;
      firstName: string;
      lastName: string;
      addressId?: string;
      addressLine1: string;
      addressLine2?: string;
      addressLine3?: string;
      city: string;
      countryCode: string;
      postalCode: string;
    };
  }
>({
  mutationFn: async ({ reservationKey, siteId, contact }) => {
    return api.post<SurfboardPaymentCredentials>(
      `/api/public/payment/${siteId}/start/wbe/${reservationKey}`,
      { contact },
    );
  },
  retry: shouldRetryPayment,
  retryDelay: RETRY_DELAY_MS,
});

// POST /api/public/payment/{siteId}/start/pre-payment/{prepaymentSlug}
export const useStartPrepayment = createMutation<
  SurfboardPaymentCredentials,
  {
    prepaymentSlug: string;
    siteId: string;
    contact: {
      email: string;
      phone: string;
      firstName: string;
      lastName: string;
      addressId?: string;
      addressLine1: string;
      addressLine2?: string;
      addressLine3?: string;
      city: string;
      countryCode: string;
      postalCode: string;
    };
  }
>({
  mutationFn: async ({ prepaymentSlug, siteId, contact }) => {
    return api.post<SurfboardPaymentCredentials>(
      `/api/public/payment/${siteId}/start/pre-payment/${prepaymentSlug}`,
      { contact },
    );
  },
  retry: shouldRetryPayment,
  retryDelay: RETRY_DELAY_MS,
});

// GET /api/public/reservations/{siteId}/{reservationKey}/payment-status
export const usePaymentStatus = createQuery<
  SurfboardPaymentStatusResponse,
  { paymentId: string; siteId: string }
>({
  queryKey: ["usePaymentStatus"],
  fetcher: async ({ paymentId, siteId }) => {
    return api.get<SurfboardPaymentStatusResponse>(
      `/api/public/payment/${siteId}/status/${paymentId}`,
    );
  },
  enabled: false,
});

// POST /api/public/payment/{siteId}/notification/{id}
export const useSendPaymentNotification = createMutation<
  void,
  { siteId: string; paymentId: string; email: string }
>({
  mutationFn: async ({ siteId, paymentId, email }) => {
    return api.post<void>(
      `/api/public/payment/${siteId}/notification/${paymentId}`,
      { email },
    );
  },
  onError: (error) => {
    handleMutationError(error);
  },
});

// GET /api/public/payment/{siteId}/receipt/{paymentId}
export const usePaymentReceipt = createQuery<
  PaymentReceiptResponse,
  { paymentId: string; siteId: string }
>({
  queryKey: ["usePaymentReceipt"],
  fetcher: async ({ paymentId, siteId }) => {
    return api.get<PaymentReceiptResponse>(
      `/api/public/payment/${siteId}/receipt/${paymentId}`,
    );
  },
  enabled: false,
});

// GET /api/public/link/{slug}/line-items
export const usePaymentLinkLineItems = createQuery<
  PaymentLinkLineItemsResponse,
  { slug: string }
>({
  queryKey: ["usePaymentLinkLineItems"],
  fetcher: async ({ slug }) => {
    return api.get<PaymentLinkLineItemsResponse>(
      `/api/public/link/${slug}/line-items`,
    );
  },
  enabled: false,
});

type RowProductAdVariables = {
  reservationKey: string;
  siteId: string;
  rowId: string;
  productAdId: string;
  languageCode: LanguageCode;
};

type RowProductAdContext = {
  previous: [readonly unknown[], GetReservationExternalResponse | undefined][];
};

const optimisticRowProductAdMutationOptions = (
  applyToSelectedIds: (currentIds: string[], productAdId: string) => string[],
) => ({
  onMutate: async ({
    rowId,
    productAdId,
  }: RowProductAdVariables): Promise<RowProductAdContext> => {
    beginReservationMutation();
    await queryClient.cancelQueries({ queryKey: GET_RESERVATION_QUERY_KEY });
    const previous = queryClient.getQueriesData<GetReservationExternalResponse>(
      {
        queryKey: GET_RESERVATION_QUERY_KEY,
      },
    );
    queryClient.setQueriesData<GetReservationExternalResponse>(
      { queryKey: GET_RESERVATION_QUERY_KEY },
      (current) => {
        if (!current) return current;
        const row = current.rows.find((r) => r.rowId === rowId);
        if (!row?.categoryAd) return current;
        const nextIds = applyToSelectedIds(
          row.categoryAd.selectedProductAdIds,
          productAdId,
        );
        return applyOptimisticReservationPatch(current, [
          { id: rowId, productAdIds: nextIds },
        ]);
      },
    );
    return { previous };
  },
  onError: (error: Error) => {
    handleMutationError(error);
  },
  onSettled: (
    _data: GetReservationExternalResponse | undefined,
    error: Error | null,
    _variables: RowProductAdVariables,
    context: RowProductAdContext | undefined,
  ) => {
    settleReservationMutation(error ? rollbackFromContext(context) : null);
  },
});

// POST /api/public/reservations/{siteId}/{reservationKey}/rows/{rowId}/product-ads
export const useAddRowProductAd = createMutation<
  GetReservationExternalResponse,
  RowProductAdVariables,
  Error,
  RowProductAdContext
>({
  mutationFn: async ({
    reservationKey,
    siteId,
    rowId,
    productAdId,
    languageCode,
  }) => {
    return api.post<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/rows/${rowId}/product-ads`,
      { productAdId },
      { languageCode },
    );
  },
  ...optimisticRowProductAdMutationOptions((currentIds, productAdId) =>
    currentIds.includes(productAdId)
      ? currentIds
      : [...currentIds, productAdId],
  ),
});

// DELETE /api/public/reservations/{siteId}/{reservationKey}/rows/{rowId}/product-ads/{productAdId}
export const useRemoveRowProductAd = createMutation<
  GetReservationExternalResponse,
  RowProductAdVariables,
  Error,
  RowProductAdContext
>({
  mutationFn: async ({
    reservationKey,
    siteId,
    rowId,
    productAdId,
    languageCode,
  }) => {
    return api.delete<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/rows/${rowId}/product-ads/${productAdId}`,
      { languageCode },
    );
  },
  ...optimisticRowProductAdMutationOptions((currentIds, productAdId) =>
    currentIds.filter((id) => id !== productAdId),
  ),
});

type RowProductAdQuantityVariables = {
  reservationKey: string;
  siteId: string;
  rowId: string;
  productAdId: string;
  quantity: number;
  dateQuantities?: Record<string, number>;
  languageCode: LanguageCode;
};

type RowProductAdQuantityContext = {
  previous: [readonly unknown[], GetReservationExternalResponse | undefined][];
};

// PATCH /api/public/reservations/{siteId}/{reservationKey}/rows/{rowId}/product-ads/{productAdId}
export const useSetRowProductAdQuantity = createMutation<
  GetReservationExternalResponse,
  RowProductAdQuantityVariables,
  Error,
  RowProductAdQuantityContext
>({
  mutationFn: async ({
    reservationKey,
    siteId,
    rowId,
    productAdId,
    quantity,
    dateQuantities,
    languageCode,
  }) => {
    return api.patch<GetReservationExternalResponse>(
      `/api/public/reservations/${siteId}/${reservationKey}/rows/${rowId}/product-ads/${productAdId}`,
      { quantity, dateQuantities },
      { languageCode },
    );
  },
  onMutate: async ({
    rowId,
    productAdId,
    quantity,
    dateQuantities,
  }): Promise<RowProductAdQuantityContext> => {
    beginReservationMutation();
    await queryClient.cancelQueries({ queryKey: GET_RESERVATION_QUERY_KEY });
    const previous = queryClient.getQueriesData<GetReservationExternalResponse>(
      {
        queryKey: GET_RESERVATION_QUERY_KEY,
      },
    );
    queryClient.setQueriesData<GetReservationExternalResponse>(
      { queryKey: GET_RESERVATION_QUERY_KEY },
      (current) => {
        if (!current) return current;
        const row = current.rows.find((r) => r.rowId === rowId);
        if (!row?.categoryAd) return current;
        const clampedQty = Math.max(0, quantity);
        const currentIds = row.categoryAd.selectedProductAdIds;
        const nextIds =
          clampedQty > 0
            ? currentIds.includes(productAdId)
              ? currentIds
              : [...currentIds, productAdId]
            : currentIds.filter((id) => id !== productAdId);
        const nextQuantities = {
          ...row.categoryAd.selectedProductAdQuantities,
          [productAdId]: clampedQty,
        };
        // Move the per-date split with the total, so a per-stay-date row never
        // sees a total its dates don't add up to while the write is in flight.
        const nextDateQuantities = dateQuantities
          ? {
              ...row.categoryAd.selectedProductAdDateQuantities,
              [productAdId]: Object.fromEntries(
                Object.entries(dateQuantities).filter(([, qty]) => qty > 0),
              ),
            }
          : row.categoryAd.selectedProductAdDateQuantities;
        return {
          ...current,
          rows: current.rows.map((r) =>
            r.rowId === rowId && r.categoryAd
              ? {
                  ...r,
                  categoryAd: {
                    ...r.categoryAd,
                    selectedProductAdIds: nextIds,
                    selectedProductAdQuantities: nextQuantities,
                    selectedProductAdDateQuantities: nextDateQuantities,
                  },
                }
              : r,
          ),
        };
      },
    );
    return { previous };
  },
  onError: (error: Error) => {
    handleMutationError(error);
  },
  onSettled: (
    _data: GetReservationExternalResponse | undefined,
    error: Error | null,
    _variables: RowProductAdQuantityVariables,
    context: RowProductAdQuantityContext | undefined,
  ) => {
    settleReservationMutation(error ? rollbackFromContext(context) : null);
  },
});
